import { computed, ref } from "vue";
import axios from "axios";

interface BounceRecommendation {
    type: "success" | "warning" | "error";
    message: string;
}

interface EmailBounceAnalytics {
    reputation_score?: number;
    reputation_status?: "excellent" | "good" | "fair" | "poor";
    hard_bounce_rate?: number;
    soft_bounce_rate?: number;
    complaint_rate?: number;
    problem_contacts_count?: number;
    recommendations?: BounceRecommendation[];
    summary?: any;
    trends?: any;
    top_reasons?: any[];
    by_type?: any;
}

const useEmailBounceAnalytics = () => {
    const bounceData = ref<EmailBounceAnalytics | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    const fetchEmailBounceAnalytics = async (emailId: number) => {
        if (!emailId) {
            error.value = "Email ID is required";
            return;
        }

        loading.value = true;
        error.value = null;

        try {
            const response = await axios.get(
                `/api/emails/${emailId}/bounce-analytics`
            );

            const data = response.data;
            bounceData.value = {
                reputation_score: data.reputation_score,
                reputation_status: data.reputation_status,
                hard_bounce_rate: data.hard_bounce_rate || 0,
                soft_bounce_rate: data.soft_bounce_rate || 0,
                complaint_rate: data.complaint_rate || 0,
                problem_contacts_count: data.problem_contacts_count || 0,
                recommendations: data.recommendations || [],
                summary: data.summary || {},
                trends: data.trends || {},
                top_reasons: data.top_reasons || [],
                by_type: data.by_type || {}
            };
        } catch (err) {
            if (err instanceof Error) {
                error.value =
                    err.message || "Failed to fetch email bounce analytics";
            } else {
                error.value = "Failed to fetch email bounce analytics";
            }
            console.error("Email bounce analytics fetch error:", err);
        } finally {
            loading.value = false;
        }
    };

    return {
        bounceData: computed(() => bounceData.value),
        loading: computed(() => loading.value),
        error: computed(() => error.value),
        fetchEmailBounceAnalytics
    };
};

export default useEmailBounceAnalytics;
